import { ArrowRightIcon } from "@talladega/icons";
import classNames from "classnames";
import { useState } from "react";
import useAftermarketPurchaseFromQuery from "@modules/purchase/warranty/hooks/useAftermarketPurchaseFromQuery";
import { Trans, useTranslation } from "next-i18next";
import CmsButton from "@modules/cms/components/CmsButton";
import { ActionButton } from "@talladega/types";
import { DynamicHeroHeadingLayout } from "@modules/cms/types/DynamicHeroHeadingLayout";
import CloudflareImagesImage from "@common/components/CloudflareImagesImage";
import { CmsHeading } from "@talladega/cms";
import { GetBackgroundStyle, IsNodeStructure } from "@talladega/helpers";
import CmsButtonModalContainer from "./CmsButtonModalContainer";

const DynamicHeroHeading = ({
  background,
  heroHeading,
  heroSubheading,
  image,
  imagePosition,
  button,
  secondaryButton,
}: DynamicHeroHeadingLayout) => {
  const { t } = useTranslation(["purchase"]);

  const [buttonModalState, setButtonModalState] = useState<{
    show: boolean;
    actionType: ActionButton;
  }>({ show: false, actionType: ActionButton.UnAssigned });

  // This can be updated to a more generalised hook overtime.
  const aftermarketPurchaseFromQuery = useAftermarketPurchaseFromQuery();
  const { year, make, model } = aftermarketPurchaseFromQuery;
  const { altText, sourceUrl } =
    (IsNodeStructure(image) ? image.node : image) ?? {};

  const baseDomain = process.env.NEXT_PUBLIC_CLOUDFLARE_IMAGES_DOMAIN || "";

  return (
    <div
      id="dynamic-hero-heading-container"
      className="no-scrollbar relative overflow-visible bg-white bg-cover bg-center"
      style={GetBackgroundStyle(background, baseDomain)}
    >
      <div
        className={classNames(
          "relative mx-auto flex max-w-page flex-col-reverse items-center px-4 pb-6 pt-10 lg:px-5 lg:py-16",
          {
            "lg:flex-row": imagePosition === "Right",
            "lg:flex-row-reverse": imagePosition === "Left",
          }
        )}
      >
        <div className="flex-1 md:px-28 lg:basis-[40%] lg:px-0">
          <div>
            <div className="w-full items-center justify-center text-center sm:items-start sm:justify-start sm:text-left">
              {year && make && model ? (
                <h1 className="leading-[2.625rem] md:leading-[3.625rem]">
                  <Trans
                    t={t}
                    i18nKey="purchase:looking_for_help"
                    values={{
                      vehicle: `${year} ${make} ${model}`,
                    }}
                    components={{
                      bold: <span className="text-primary-500" />,
                    }}
                  />
                </h1>
              ) : (
                heroHeading && (
                  <CmsHeading
                    headingLevel={heroHeading.headingGroup?.headingLevel}
                    textSize={heroHeading.headingGroup?.textSize}
                    text={heroHeading.headingGroup?.text}
                    htmlId={heroHeading.headingGroup?.htmlId}
                  />
                )
              )}
            </div>

            <div className="mt-2 w-full items-center justify-center text-center sm:items-start sm:justify-start sm:text-left">
              {heroSubheading && (
                <div className="mt-4 lg:mt-8">
                  <CmsHeading
                    headingLevel={heroSubheading.headingGroup?.headingLevel}
                    textSize={heroSubheading.headingGroup?.textSize}
                    text={heroSubheading.headingGroup?.text}
                    htmlId={heroSubheading.headingGroup?.htmlId}
                  />
                </div>
              )}
            </div>

            <div className="mt-8 flex flex-col-reverse items-center justify-center gap-3 text-center sm:flex-row sm:items-start sm:justify-start sm:text-left">
              {button && !button.isHidden && (
                <div>
                  <CmsButton
                    button={button}
                    rightIcon={<ArrowRightIcon />}
                    setButtonModalState={setButtonModalState}
                  />
                </div>
              )}
              {secondaryButton && !secondaryButton.isHidden && (
                <div>
                  <CmsButton
                    button={secondaryButton}
                    rightIcon={<ArrowRightIcon />}
                    setButtonModalState={setButtonModalState}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="relative w-full flex-1 lg:basis-[60%]">
          <div className="relative inset-0 h-auto min-w-full">
            <div className="relative h-56 overflow-auto md:h-96">
              {image && sourceUrl && (
                <CloudflareImagesImage
                  src={sourceUrl}
                  alt={altText}
                  fill
                  className="z-[2] h-auto w-full rounded-lg object-contain"
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {buttonModalState.show && (
        <CmsButtonModalContainer buttonModalState={buttonModalState} />
      )}
    </div>
  );
};

export default DynamicHeroHeading;
